import cytoscape, { type Core, type ElementDefinition } from 'cytoscape';
import { Network, TriangleAlert } from 'lucide-react';
import { useEffect, useMemo, useRef } from 'react';

import type { ContextResult } from '../api/symbols.js';

interface SubgraphViewerProps {
  readonly context: ContextResult | null;
  readonly maxNodes?: number;
}

export function SubgraphViewer({ context, maxNodes = 150 }: SubgraphViewerProps): JSX.Element {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const cyRef = useRef<Core | null>(null);

  const elements = useMemo<ElementDefinition[]>(() => {
    if (!context?.subgraph) return [];
    const nodes = context.subgraph.nodes.slice(0, maxNodes);
    const allowed = new Set(nodes.map((n) => n.id));
    const edges = context.subgraph.edges.filter(
      (e) => allowed.has(e.source) && allowed.has(e.target),
    );
    const nodeEls: ElementDefinition[] = nodes.map((n) => ({
      data: { id: n.id, label: n.label ?? n.id, kind: n.kind ?? 'node' },
    }));
    const edgeEls: ElementDefinition[] = edges.map((e, i) => ({
      data: {
        id: `e${String(i)}`,
        source: e.source,
        target: e.target,
        type: e.type ?? 'rel',
      },
    }));
    return [...nodeEls, ...edgeEls];
  }, [context, maxNodes]);

  const truncated = useMemo(() => {
    return (context?.subgraph?.nodes.length ?? 0) > maxNodes;
  }, [context, maxNodes]);

  useEffect(() => {
    if (!containerRef.current) return undefined;
    const cy = cytoscape({
      container: containerRef.current,
      elements,
      style: [
        {
          selector: 'node',
          style: {
            'background-color': '#7c3aed',
            'border-color': '#a78bfa',
            'border-width': 1,
            label: 'data(label)',
            color: '#e4e4ed',
            'font-size': 10,
            'text-valign': 'bottom',
            'text-halign': 'center',
            'text-margin-y': 4,
            'text-outline-color': '#06060a',
            'text-outline-width': 2,
          },
        },
        {
          selector: 'edge',
          style: {
            'line-color': '#2a2a3a',
            'target-arrow-color': '#5a5a70',
            'target-arrow-shape': 'triangle',
            'curve-style': 'bezier',
            width: 1.5,
          },
        },
      ],
      layout: { name: 'cose', animate: false },
    });
    cyRef.current = cy;
    return () => {
      cy.destroy();
      cyRef.current = null;
    };
  }, [elements]);

  if (!context) {
    return (
      <div className="grid place-items-center rounded-2xl border border-dashed border-border-subtle bg-surface/40 px-6 py-16 text-center">
        <Network className="h-8 w-8 text-text-muted" />
        <p className="mt-3 text-sm text-text-muted">Pick a symbol to see its neighborhood.</p>
      </div>
    );
  }
  if (elements.length === 0) {
    return (
      <p className="rounded-md border border-dashed border-border-subtle bg-surface/40 p-3 text-sm text-text-muted">
        No subgraph data for this symbol.
      </p>
    );
  }
  return (
    <div className="grid gap-2 rounded-2xl border border-border-subtle bg-surface/60 p-3 backdrop-blur">
      {truncated && (
        <p className="inline-flex items-center gap-2 self-start rounded-md border border-amber-500/30 bg-amber-500/10 px-2 py-1 text-[11px] text-amber-300">
          <TriangleAlert className="h-3 w-3" />
          Capped at {maxNodes} nodes. This is a hub symbol — expand on click for more.
        </p>
      )}
      <div
        ref={containerRef}
        className="h-[440px] w-full rounded-xl border border-border-subtle bg-deep"
      />
    </div>
  );
}
